import { Card, CardContent } from '@/components/ui/card'
import type { EmployeeProfile } from '@/types'
import { User } from 'lucide-react'

interface AdditionalInformationProps {
  employee: EmployeeProfile
}

/**
 * Additional Information Component
 * 
 * Displays comprehensive employee metadata in a structured grid layout:
 * - Developer information and verification status
 * - Release date and category information
 * - Technical specifications (size, age rating, version)
 * - Supported languages with expandable list
 */
export function AdditionalInformation({ employee }: AdditionalInformationProps) {
  return (
    <Card className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <CardContent>
        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-medium mb-1 flex items-center gap-1">
              <User className="w-3 h-3" />
              Developed by
            </h4>
            <p className="text-sm text-muted-foreground">{employee.developer.name}</p>
          </div>
          <div>
            <h4 className="text-sm font-medium mb-1">Release date</h4>
            <p className="text-sm text-muted-foreground">
              {employee.releasedOn.toLocaleDateString()}
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium mb-1">Category</h4>
            <p className="text-sm text-muted-foreground">{employee.category.name}</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-medium mb-1">Approximate size</h4>
            <p className="text-sm text-muted-foreground">208.1 MB</p>
          </div>
          <div>
            <h4 className="text-sm font-medium mb-1">Age rating</h4>
            <p className="text-sm text-muted-foreground">For all ages</p>
          </div>
          <div>
            <h4 className="text-sm font-medium mb-1">Version</h4>
            <p className="text-sm text-muted-foreground">{employee.version}</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-medium mb-1">Supported languages</h4>
            <div className="text-sm text-muted-foreground space-y-1">
              {employee.languages.slice(0, 5).map((lang, index) => (
                <p key={index}>{lang}</p>
              ))}
              {employee.languages.length > 5 && (
                <p className="text-sm text-blue-600 cursor-pointer hover:underline">
                  Read more
                </p>
              )}
            </div>
          </div>
        </div>

      </CardContent>

    </Card>
  )
}

AdditionalInformation.displayName = "AdditionalInformation"